export function normalizeOption(value) {
  return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/ł/g, 'l').trim();
}

export function frameSelection(options) {
  const values = options.map(normalizeOption);
  const color = values.includes('czarny') || values.includes('black') ? 'black'
    : values.includes('bialy') || values.includes('white') ? 'white'
      : values.includes('zloty') || values.includes('gold') ? 'gold'
        : values.includes('srebrny') || values.includes('silver') ? 'silver' : null;
  const size = values.includes('a4') ? 'A4' : values.includes('a3') ? 'A3' : null;
  return color && size ? { color, size } : null;
}

const COLORS = ['black', 'white', 'gold', 'silver'];
const SIZES = ['A4', 'A3'];
export const SCENE_NAMES = Object.freeze(['wall', 'console', 'flatlay']);
const EPSILON = 1e-10;

export function validQuad(quad) {
  if (!Array.isArray(quad) || quad.length !== 4 || quad.some(point => !Array.isArray(point) || point.length !== 2 || point.some(value => !Number.isFinite(value) || value < 0 || value > 1))) return false;
  return quad.every((point, index) => {
    const next = quad[(index + 1) % 4];
    const after = quad[(index + 2) % 4];
    return (next[0] - point[0]) * (after[1] - next[1]) - (next[1] - point[1]) * (after[0] - next[0]) > EPSILON;
  });
}

export function homographyMatrix3d(quad, width, height = width) {
  if (!validQuad(quad) || !Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;
  const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = quad;
  const dx1 = x1 - x2;
  const dx2 = x3 - x2;
  const dx3 = x0 - x1 + x2 - x3;
  const dy1 = y1 - y2;
  const dy2 = y3 - y2;
  const dy3 = y0 - y1 + y2 - y3;
  let g = 0;
  let h = 0;
  if (Math.abs(dx3) > EPSILON || Math.abs(dy3) > EPSILON) {
    const determinant = dx1 * dy2 - dx2 * dy1;
    if (Math.abs(determinant) <= EPSILON) return null;
    g = (dx3 * dy2 - dx2 * dy3) / determinant;
    h = (dx1 * dy3 - dx3 * dy1) / determinant;
  }
  if ([1, 1 + g, 1 + g + h, 1 + h].some(value => value <= EPSILON || !Number.isFinite(value))) return null;
  const a = x1 - x0 + g * x1;
  const b = x3 - x0 + h * x3;
  const d = y1 - y0 + g * y1;
  const e = y3 - y0 + h * y3;
  const determinant = a * (e - y0 * h) - b * (d - y0 * g) + x0 * (d * h - e * g);
  if (!Number.isFinite(determinant) || Math.abs(determinant) <= EPSILON) return null;
  const matrix = [a, d * height / width, 0, g / width, b, e * height / width, 0, h / width, 0, 0, 1, 0, x0 * width, y0 * height, 0, 1];
  return matrix.every(Number.isFinite) ? matrix : null;
}

function validAsset(value) {
  return typeof value === 'string' && /^(https:\/\/|\/\/|\/(?!\/))\S+$/.test(value);
}

export function selectMockups(manifest, selection) {
  if (!SIZES.includes(selection?.size) || !COLORS.includes(selection?.color)) return null;
  const entries = manifest?.[selection.size]?.[selection.color];
  if (!entries || typeof entries !== 'object') return null;
  const result = {};
  for (const name of SCENE_NAMES) {
    const entry = entries[name];
    if (!validAsset(entry?.thumb) || !validAsset(entry?.large) || !validQuad(entry?.quad) || !homographyMatrix3d(entry.quad, 1)) return null;
    result[name] = { thumb: entry.thumb, large: entry.large, quad: entry.quad.map(point => [...point]) };
  }
  return result;
}

// Embed the printed design so Teeinblue cannot empty an existing thumbnail
// by replacing the live SVG referenced by its <use> element.
export function captureArtwork(artwork, source) {
  if (!artwork || !source?.cloneNode || !source.id) return null;
  const images = [...source.querySelectorAll('image')];
  if (!images.some(image => image.getAttribute('href') || image.getAttribute('xlink:href'))) return null;
  const snapshot = artwork.cloneNode(true);
  const defs = artwork.ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'defs');
  const printedDesign = source.cloneNode(true);
  printedDesign.removeAttribute('class');
  defs.append(printedDesign);
  snapshot.prepend(defs);
  return snapshot;
}

export function createArtwork(artwork, prefix) {
  const clone = artwork.cloneNode(true);
  for (const attribute of ['id', 'class', 'style', 'x', 'y']) clone.removeAttribute(attribute);
  clone.setAttribute('class', 'tp-lifestyle__artwork');
  clone.setAttribute('width', '100%');
  clone.setAttribute('height', '100%');
  clone.setAttribute('preserveAspectRatio', 'none');
  clone.setAttribute('overflow', 'hidden');
  clone.setAttribute('aria-hidden', 'true');
  clone.setAttribute('focusable', 'false');
  const ids = new Map();
  clone.querySelectorAll('[id]').forEach(node => {
    const old = node.id;
    const fresh = `${prefix}-${old}`;
    ids.set(old, fresh);
    node.id = fresh;
  });
  clone.querySelectorAll('*').forEach(node => {
    [...node.attributes].forEach(attribute => {
      let value = attribute.value;
      for (const [old, fresh] of ids) {
        if (value === `#${old}`) value = `#${fresh}`;
        value = value.replaceAll(`url(#${old})`, `url(#${fresh})`);
      }
      if (value !== attribute.value) node.setAttribute(attribute.name, value);
    });
  });
  return clone;
}

export function loadSceneImage(image, url, signal) {
  if (!image || signal?.aborted) return Promise.resolve(false);
  return new Promise(resolve => {
    let settled = false;
    let timeout;
    const finish = result => {
      if (settled) return;
      settled = true;
      image.removeEventListener('load', loaded);
      image.removeEventListener('error', failed);
      signal?.removeEventListener('abort', failed);
      clearTimeout(timeout);
      resolve(result);
    };
    const loaded = () => {
      if (!image.complete || !image.naturalWidth || !image.naturalHeight) return;
      const expected = new URL(url, image.ownerDocument?.baseURI || 'https://invalid.local/').href;
      const actual = new URL(image.currentSrc || image.src, image.ownerDocument?.baseURI || 'https://invalid.local/').href;
      if (actual === expected) finish(true);
    };
    const failed = () => finish(false);
    image.addEventListener('load', loaded);
    image.addEventListener('error', failed);
    signal?.addEventListener('abort', failed, { once: true });
    timeout = setTimeout(failed, 12000);
    image.loading = 'eager';
    image.decoding = 'async';
    image.removeAttribute('srcset');
    image.src = url;
    loaded();
  });
}

class TapandaLifestyleGallery extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.root = this.closest('[id^="MainProduct-"]') || this.closest('.shopify-section') || document;
    try {
      this.config = JSON.parse(this.querySelector('[data-lifestyle-variants]').textContent);
      this.mockups = JSON.parse(this.querySelector('[data-lifestyle-mockups]').textContent);
      if (!Array.isArray(this.config.variants)) throw new Error('Invalid variants');
    } catch { this.hidden = true; return; }
    this.variantId = this.config.initial;
    this.dialog = this.querySelector('dialog');
    this.cards = [...this.querySelectorAll('[data-lifestyle-scene]')];
    if (!this.dialog || this.cards.length !== 3 || SCENE_NAMES.some(name => this.cards.filter(card => card.dataset.lifestyleScene === name).length !== 1)) { this.hidden = true; return; }
    const grid = this.querySelector('.tp-lifestyle__grid');
    if (grid) {
      const displayOrder = ['flatlay', 'console', 'wall'];
      this.cards.sort((a, b) => displayOrder.indexOf(a.dataset.lifestyleScene) - displayOrder.indexOf(b.dataset.lifestyleScene));
      this.cards.forEach(card => grid.append(card));
    }
    this.sceneMeta = new Map();
    this.sceneLoadKey = null;
    this.ready = false;
    const { signal } = this.abort;
    this.ensureNavigation();
    this.media = this.root.querySelector('.product__media-wrapper');
    if (this.media && !this.homeAnchor) {
      this.homeAnchor = document.createComment('lifestyle-gallery');
      this.media.insertBefore(this.homeAnchor, this);
    }
    this.mobile = window.matchMedia('(max-width: 749px)');
    this.mobile.addEventListener('change', () => this.placeGallery(), { signal });
    this.addEventListener('click', event => {
      const navigation = event.target.closest('[data-lifestyle-direction]');
      if (navigation && this.dialog.contains(navigation)) {
        event.preventDefault();
        event.stopPropagation();
        this.navigatePreview(Number(navigation.dataset.lifestyleDirection));
        return;
      }
      const card = event.target.closest('[data-lifestyle-scene]');
      if (card && this.contains(card)) this.openPreview(card);
      if (event.target.closest('[data-lifestyle-close]')) this.dialog.close();
      if (event.target === this.dialog) {
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }
    }, { signal });
    this.dialog.addEventListener('close', () => {
      this.dialogLoad?.abort();
      this.dialog.removeAttribute('aria-busy');
      this.activePreviewCard = null;
      this.displayedPreviewCard = null;
      this.clearExpanded();
      this.observeScenes();
      if (this.isConnected) this.lastTrigger?.focus({ preventScroll: true });
    }, { signal });
    this.dialog.addEventListener('keydown', event => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey
        || event.target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])')) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      event.stopPropagation();
      this.navigatePreview(event.key === 'ArrowRight' ? 1 : -1);
    }, { signal });
    this.root.addEventListener('change', () => { this.hideChangedSelection(); this.schedule(); }, { signal });
    this.root.addEventListener('load', event => {
      if (event.target?.matches?.('.tee-mockup-artwork image')) this.schedule();
    }, { signal, capture: true });
    document.addEventListener('teeinblue-event-variant-changed', event => {
      const id = Number(event.detail?.variantId);
      if (this.config.variants.some(variant => variant.id === id)) this.variantId = id;
      this.hideChangedSelection();
      this.schedule();
    }, { signal });
    for (const name of ['teeinblue-event-customization-changed', 'teeinblue-event-component-injected']) document.addEventListener(name, () => this.schedule(), { signal });
    this.observer = new MutationObserver(records => {
      if (this.previewMutationsMatter(records)) this.schedule();
    });
    this.observer.observe(this.root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'src', 'href', 'xlink:href', 'checked', 'aria-busy', 'data-tp-cc-gallery-url'] });
    if (typeof ResizeObserver !== 'undefined') this.resizeObserver = new ResizeObserver(() => this.refreshPlanes());
    else window.addEventListener('resize', () => this.refreshPlanes(), { signal });
    this.observeScenes();
    this.restoreCustomerArtwork();
    this.sync();
    this.placeGallery();
  }

  previewMutationsMatter(records) {
    const external = records.filter(record => !this.contains(record.target));
    if (!external.length) return false;
    if (!this.root.isConnected || this.hasAttribute('data-preview-busy') || external.some(record => record.target === this.root)) return true;
    const form = this.root.querySelector('#teeFormInputs');
    const media = this.root.querySelector('#tee-gallery');
    const reference = media?.querySelector('.tee-slide--active .tee-mockup-item--loaded .tee-printarea--printarea svg.tee-printarea-artwork use');
    const href = reference?.getAttribute('href') || reference?.getAttribute('xlink:href');
    const source = href?.startsWith('#') ? document.getElementById(href.slice(1)) : null;
    // During injection/replacement or a busy upload, keep the original broad
    // observation until the live preview and its source are available again.
    if (!form || !media || !source?.matches('svg.tee-mockup-artwork')) return true;
    const inputs = this.root.querySelector('#tee-artwork-form') || form;
    const busy = [...this.root.querySelectorAll('.tee-photo-uploading, .tee-loading, .tee-ai-generating')];
    const dependencies = [inputs, form, media, source, ...busy];
    // A detached subtree is no longer found by the live queries above. Inspect
    // both sides so replacing a form, SVG source or media wrapper still syncs.
    const relevant = '#teeFormInputs, #tee-artwork-form, #tee-gallery, .tee-photo-uploading, .tee-loading, .tee-ai-generating';
    return external.some(record => {
      const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
      if (!target || dependencies.some(node => node.contains(target) || target.contains(node))) return true;
      return [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && (node.matches(relevant) || node.querySelector(relevant)));
    });
  }

  disconnectedCallback() {
    this.removeAttribute('data-customer-artwork-ready');
    this.abort?.abort();
    this.abort = null;
    this.sceneLoad?.abort();
    this.dialogLoad?.abort();
    this.dialog?.removeAttribute('aria-busy');
    this.activePreviewCard = null;
    this.displayedPreviewCard = null;
    this.artworkLoad?.abort();
    this.artworkLoad = null;
    this.artworkLoadKey = null;
    clearTimeout(this.sceneRetry);
    this.sceneRetry = null;
    this.observer?.disconnect();
    this.artObserver?.disconnect();
    this.resizeObserver?.disconnect();
    this.artSource = null;
    this.sceneLoadKey = null;
    this.ready = false;
    clearTimeout(this.pending);
    this.pending = null;
    if (this.dialog?.open) this.dialog.close();
    this.clearExpanded();
    this.sceneMeta?.clear();
    this.clearArtwork();
  }

  placeGallery() {
    if (!this.media || !this.homeAnchor?.isConnected) return;
    if (this.mobile.matches && this.parentElement === this.media) this.media.after(this);
    else if (!this.mobile.matches && this.parentElement !== this.media) this.homeAnchor.after(this);
  }

  schedule() {
    if (this.pending) return;
    this.pending = setTimeout(() => { this.pending = null; this.sync(); }, 120);
  }

  selectedVariant() {
    const checked = [...this.root.querySelectorAll('#teeFormInputs input:checked')].map(input => normalizeOption(input.value));
    if (checked.some(value => /^a\d+$/.test(value)) && !frameSelection(checked)) return null;
    const matches = this.config.variants.filter(variant => {
      const selection = frameSelection(variant.options);
      return selection && checked.includes(normalizeOption(variant.options.find(value => normalizeOption(value) === selection.size.toLowerCase())))
        && variant.options.some(value => checked.includes(normalizeOption(value)) && ['czarny', 'bialy', 'zloty', 'srebrny', 'black', 'white', 'gold', 'silver'].includes(normalizeOption(value)));
    });
    if (matches.length === 1) return matches[0];
    if (frameSelection(checked)) return null;
    return this.config.variants.find(variant => variant.id === this.variantId);
  }

  sourcePreview(selection) {
    if (!SIZES.includes(selection?.size) || !COLORS.includes(selection?.color)) return null;
    const active = this.root.querySelector('#tee-gallery .tee-slide--active .tee-mockup-item--loaded');
    const artwork = active?.querySelector('.tee-printarea--printarea svg.tee-printarea-artwork');
    const use = artwork?.querySelector('use');
    const reference = use?.getAttribute('href') || use?.getAttribute('xlink:href');
    if (!reference?.startsWith('#')) return null;
    const source = document.getElementById(reference.slice(1));
    return source?.matches('svg.tee-mockup-artwork') ? { artwork, source } : null;
  }

  customerArtworkUrl() {
    if (!this.root.matches?.('[data-tp-custom-changes-photo][data-tp-custom-changes-text]')) return null;
    return this.root.getAttribute('data-tp-cc-gallery-url') || '';
  }

  matchesCustomerArtwork(preview, url) {
    return !!url && [...preview?.source?.querySelectorAll('image') || []]
      .some(image => (image.getAttribute('href') || image.getAttribute('xlink:href')) === url);
  }

  restoreCustomerArtwork() {
    const customerUrl = this.customerArtworkUrl();
    if (customerUrl === null) return;
    if (!customerUrl || (this.savedCustomerRoot && this.savedCustomerRoot !== this.root)) {
      this.savedCustomerPreview = null;
      this.savedCustomerKey = null;
      this.savedCustomerRoot = null;
    } else if (!this.preview && this.savedCustomerPreview) {
      // Moving the native custom element at a mobile breakpoint runs disconnect
      // cleanup. Restore only its detached, already loaded customer snapshot.
      this.renderArtwork(this.savedCustomerPreview, this.savedCustomerKey);
      this.setAttribute('data-customer-artwork-ready', '');
      this.hidden = false;
    }
  }

  clearArtwork() {
    this.querySelectorAll('[data-lifestyle-art]').forEach(target => target.replaceChildren());
    this.artKey = null;
    this.preview = null;
  }

  renderArtwork(preview, key) {
    if (key === this.artKey) return;
    this.querySelectorAll('[data-lifestyle-art]').forEach((target, index) => target.replaceChildren(createArtwork(preview.artwork, `tp-art-${this.dataset.sectionId}-${index}`)));
    this.preview = preview;
    this.artKey = key;
  }

  async prepareArtwork(preview, key) {
    const customerUrl = this.customerArtworkUrl();
    this.artworkLoad?.abort();
    this.artworkLoadKey = key;
    const snapshot = captureArtwork(preview.artwork, preview.source);
    if (!snapshot) return;
    const load = this.artworkLoad = new AbortController();
    const urls = [...new Set([...snapshot.querySelectorAll('image')].map(image => image.getAttribute('href') || image.getAttribute('xlink:href')).filter(Boolean))];
    const results = await Promise.all(urls.map(url => loadSceneImage(this.ownerDocument.createElement('img'), url, load.signal)));
    if (load.signal.aborted || !this.isConnected || this.artworkLoad !== load || this.artworkLoadKey !== key || this.hasAttribute('data-preview-busy')) return;
    if (results.some(result => !result)) return;
    if (customerUrl !== null && (customerUrl !== this.customerArtworkUrl() || !this.matchesCustomerArtwork(preview, customerUrl))) return;
    const readyPreview = { artwork: snapshot };
    if (!this.defaultPreview) { this.defaultPreview = readyPreview; this.defaultArtKey = key; }
    this.renderArtwork(readyPreview, key);
    if (customerUrl !== null) {
      this.savedCustomerPreview = readyPreview;
      this.savedCustomerKey = key;
      this.savedCustomerRoot = this.root;
      this.setAttribute('data-customer-artwork-ready', '');
      this.hidden = false;
    }
    this.refreshPlanes();
  }

  clearExpanded() {
    const expanded = this.querySelector?.('[data-lifestyle-expanded]');
    expanded?.querySelectorAll('[data-scene]').forEach(scene => this.sceneMeta?.delete(scene));
    expanded?.replaceChildren();
  }

  hideChangedSelection() {
    const variant = this.selectedVariant();
    const selection = variant && frameSelection(variant.options);
    if (selection?.color === this.dataset.frameColor && selection?.size === this.dataset.frameSize) return;
    this.sceneLoad?.abort();
    clearTimeout(this.sceneRetry);
    this.sceneRetry = null;
    this.sceneLoadKey = null;
    if (this.dialog.open) this.dialog.close();
    this.setCardsReady(false);
  }

  setCardsReady(ready) {
    this.ready = ready;
    this.toggleAttribute('data-scenes-loading', !ready);
    this.cards.forEach(card => { card.style.visibility = ready ? '' : 'hidden'; card.disabled = !ready || this.hasAttribute('data-preview-busy'); });
  }

  setReflection(scene, entry, url) {
    const reflection = scene.querySelector('[data-lifestyle-reflection]');
    if (!reflection) return;
    reflection.style.backgroundImage = `url(${JSON.stringify(url)})`;
    reflection.style.clipPath = `polygon(${entry.quad.map(([x, y]) => `${x * 100}% ${y * 100}%`).join(',')})`;
  }

  async prepareScenes(entries, key, attempt = 0) {
    clearTimeout(this.sceneRetry);
    this.sceneRetry = null;
    this.sceneLoad?.abort();
    this.sceneLoad = new AbortController();
    const load = this.sceneLoad;
    this.sceneLoadKey = key;
    if (this.dialog.open) this.dialog.close();
    this.setCardsReady(false);
    this.hidden = false;
    const tasks = this.cards.map(card => {
      const scene = card.querySelector('[data-scene]');
      const image = scene?.querySelector('img.tp-lifestyle__background');
      const entry = entries[card.dataset.lifestyleScene];
      if (!scene || !image || !scene.querySelector('[data-lifestyle-art]')) return Promise.resolve(false);
      this.sceneMeta.set(scene, entry);
      this.setReflection(scene, entry, entry.thumb);
      return loadSceneImage(image, entry.thumb, load.signal);
    });
    const results = await Promise.all(tasks);
    if (load.signal.aborted || !this.isConnected || this.sceneLoad !== load || this.sceneLoadKey !== key) return;
    if (results.some(result => !result)) {
      this.hidden = true;
      if (attempt === 0) this.sceneRetry = setTimeout(() => {
        this.sceneRetry = null;
        if (!load.signal.aborted && this.isConnected && this.sceneLoadKey === key) this.prepareScenes(entries, key, 1);
      }, 1200);
      return;
    }
    this.setCardsReady(true);
    this.refreshPlanes();
  }

  observeScenes() {
    this.resizeObserver?.disconnect();
    this.querySelectorAll('[data-scene]').forEach(scene => this.resizeObserver?.observe(scene));
  }

  refreshPlanes() {
    this.querySelectorAll('[data-scene]').forEach(scene => {
      const target = scene.querySelector('[data-lifestyle-art]');
      const entry = this.sceneMeta?.get(scene);
      if (!target || !entry) return;
      const width = scene.clientWidth;
      const matrix = homographyMatrix3d(entry.quad, width, scene.clientHeight);
      target.style.visibility = matrix ? '' : 'hidden';
      if (!matrix) return;
      target.style.width = `${width}px`;
      target.style.height = `${width}px`;
      target.style.transformOrigin = '0 0';
      target.style.transform = `matrix3d(${matrix.join(',')})`;
    });
  }

  sync() {
    if (!this.isConnected) return;
    const variant = this.selectedVariant();
    const selection = variant && frameSelection(variant.options);
    const entries = selectMockups(this.mockups, selection);
    const preview = entries && this.sourcePreview(selection);
    const customerUrl = this.customerArtworkUrl();
    this.restoreCustomerArtwork();
    // Only removal/no customer result clears the saved display. A description
    // edit, in-flight generation or failed replacement retains the last snapshot.
    if (customerUrl !== null && (!customerUrl || (!this.preview && !this.matchesCustomerArtwork(preview, customerUrl)))) {
      this.removeAttribute('data-customer-artwork-ready');
      this.hidden = true;
      this.sceneLoad?.abort();
      clearTimeout(this.sceneRetry);
      this.sceneRetry = null;
      this.sceneLoadKey = null;
      this.setCardsReady(false);
      this.artworkLoad?.abort();
      this.artworkLoadKey = null;
      this.clearArtwork();
      if (this.dialog.open) this.dialog.close();
      return;
    }
    if (!entries) {
      this.sceneLoad?.abort();
      clearTimeout(this.sceneRetry);
      this.sceneRetry = null;
      this.sceneLoadKey = null;
      this.setCardsReady(false);
      this.hidden = true;
      this.clearArtwork();
      this.artworkLoad?.abort();
      this.artworkLoadKey = null;
      this.artObserver?.disconnect();
      this.artSource = null;
      if (this.dialog.open) this.dialog.close();
      return;
    }
    this.variantId = variant.id;
    this.dataset.frameColor = selection.color;
    this.dataset.frameSize = selection.size;
    const selectionNode = this.querySelector('[data-lifestyle-selection]');
    const selectionLabel = variant.options.filter(value => normalizeOption(value) === selection.size.toLowerCase() || ['czarny', 'bialy', 'zloty', 'srebrny', 'black', 'white', 'gold', 'silver'].includes(normalizeOption(value))).join(' · ');
    if (selectionNode.textContent !== selectionLabel) selectionNode.textContent = selectionLabel;
    const busy = [...this.root.querySelectorAll('#tee-artwork-form [aria-busy="true"], #tee-gallery [aria-busy="true"], .tee-photo-uploading, .tee-loading, .tee-ai-generating')].some(node => node.getClientRects().length > 0);
    this.toggleAttribute('data-preview-busy', busy);
    this.cards.forEach(card => { card.disabled = !this.ready || busy; });
    const sceneKey = `${variant.id}:${JSON.stringify(entries)}`;
    if (sceneKey !== this.sceneLoadKey) this.prepareScenes(entries, sceneKey);
    if (customerUrl === null && !this.preview && this.defaultPreview) this.renderArtwork(this.defaultPreview, this.defaultArtKey);
    const matchingPreview = customerUrl === null || this.matchesCustomerArtwork(preview, customerUrl);
    if (busy || !preview || !matchingPreview) {
      this.artworkLoad?.abort();
      this.artworkLoadKey = null;
      if (customerUrl === null && this.defaultPreview) this.renderArtwork(this.defaultPreview, this.defaultArtKey);
      if (busy && this.dialog.open) this.dialog.close();
    } else {
      const key = `${preview.artwork.outerHTML}:${preview.source.outerHTML}`;
      if (key !== this.artKey && key !== this.artworkLoadKey) this.prepareArtwork(preview, key);
      // Reparenting at the mobile breakpoint must retain the saved design.
      else if (customerUrl === null && !this.preview && this.defaultPreview) this.renderArtwork(this.defaultPreview, this.defaultArtKey);
    }
    if (preview && this.artSource !== preview.source) {
      this.artObserver?.disconnect();
      this.artSource = preview.source;
      this.artObserver = new MutationObserver(() => this.schedule());
      this.artObserver.observe(preview.source, { attributes: true, childList: true, subtree: true, characterData: true });
    }
    this.refreshPlanes();
    if (this.dialog.open) this.updateCaption();
  }

  updateCaption() {
    const card = this.displayedPreviewCard || this.activePreviewCard || this.lastTrigger;
    const caption = card?.querySelector('.tp-lifestyle__caption')?.textContent.trim() || '';
    this.querySelector('[data-lifestyle-dialog-caption]').textContent = caption + ' · ' + this.querySelector('[data-lifestyle-selection]').textContent;
    const position = this.cards.indexOf(card);
    this.querySelector('[data-lifestyle-navigation-status]').textContent = position < 0 ? '' : `Zdjęcie ${position + 1} z ${this.cards.length}: ${caption}`;
  }

  ensureNavigation() {
    if (this.dialog.querySelector('[data-lifestyle-direction]')) return;
    const controls = document.createElement('div');
    controls.className = 'tp-lifestyle__navigation';
    controls.innerHTML = '<button type="button" class="tp-lifestyle__nav tp-lifestyle__nav--previous" data-lifestyle-direction="-1" aria-label="Poprzednie zdjęcie"><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m15 5-7 7 7 7"/></svg></button><button type="button" class="tp-lifestyle__nav tp-lifestyle__nav--next" data-lifestyle-direction="1" aria-label="Następne zdjęcie"><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m9 5 7 7-7 7"/></svg></button><span class="tp-lifestyle__navigation-status" data-lifestyle-navigation-status role="status" aria-live="polite" aria-atomic="true"></span>';
    this.dialog.appendChild(controls);
  }

  navigatePreview(direction) {
    if (!this.dialog.open || ![-1, 1].includes(direction)) return;
    const current = this.activePreviewCard || this.displayedPreviewCard || this.lastTrigger;
    const index = this.cards.indexOf(current);
    if (index < 0) return;
    const next = this.cards[(index + direction + this.cards.length) % this.cards.length];
    void this.openPreview(next);
  }

  getPreviewItems() {
    if (!this.isConnected || this.hidden || !this.ready || !this.preview || this.hasAttribute('data-preview-busy')) return [];
    return this.cards.filter(card => !card.disabled && this.sceneMeta.has(card.querySelector('[data-scene]')))
      .map(card => ({ key: card.dataset.lifestyleScene, label: card.querySelector('.tp-lifestyle__caption')?.textContent.trim() || '' }));
  }

  async preparePreview(key, signal, prefix) {
    if (!this.getPreviewItems().some(item => item.key === key) || signal.aborted) return null;
    const card = this.cards.find(item => item.dataset.lifestyleScene === key);
    const original = card.querySelector('[data-scene]');
    const entry = this.sceneMeta.get(original);
    const scene = original.cloneNode(true);
    const image = scene.querySelector('img.tp-lifestyle__background');
    const target = scene.querySelector('[data-lifestyle-art]');
    if (!entry || !image || !target) return null;
    target.replaceChildren(createArtwork(this.preview.artwork, prefix));
    this.setReflection(scene, entry, entry.large);
    const width = 740;
    const matrix = homographyMatrix3d(entry.quad, width, width);
    if (!matrix) return null;
    scene.style.width = `${width}px`;
    scene.style.height = `${width}px`;
    target.style.visibility = '';
    target.style.width = `${width}px`;
    target.style.height = `${width}px`;
    target.style.transformOrigin = '0 0';
    target.style.transform = `matrix3d(${matrix.join(',')})`;
    const loaded = await loadSceneImage(image, entry.large, signal);
    return loaded && !signal.aborted ? { node: scene, width, height: width } : null;
  }

  async openPreview(card) {
    if (this.hidden || !this.ready || this.hasAttribute('data-preview-busy') || !this.dialog.showModal) return;
    const shared = new CustomEvent('tapanda:product-gallery-open', {
      bubbles: true, cancelable: true, detail: { key: card.dataset.lifestyleScene, trigger: card }
    });
    if (!this.dispatchEvent(shared)) return;
    const original = card.querySelector('[data-scene]');
    const entry = this.sceneMeta.get(original);
    if (!entry || !this.preview) return;
    this.dialogLoad?.abort();
    this.dialogLoad = new AbortController();
    const load = this.dialogLoad;
    const wasOpen = this.dialog.open;
    if (!wasOpen) this.lastTrigger = card;
    this.activePreviewCard = card;
    const scene = original.cloneNode(true);
    const image = scene.querySelector('img.tp-lifestyle__background');
    const target = scene.querySelector('[data-lifestyle-art]');
    if (!image || !target) return;
    scene.style.visibility = 'hidden';
    target.replaceChildren(createArtwork(this.preview.artwork, `tp-art-${this.dataset.sectionId}-dialog`));
    this.sceneMeta.set(scene, entry);
    this.setReflection(scene, entry, entry.large);
    this.dialog.setAttribute('aria-busy', 'true');
    if (!wasOpen) {
      this.clearExpanded();
      this.querySelector('[data-lifestyle-expanded]').replaceChildren(scene);
      this.updateCaption();
      this.dialog.showModal();
      this.observeScenes();
      this.refreshPlanes();
    }
    const loaded = await loadSceneImage(image, entry.large, load.signal);
    if (load.signal.aborted || !this.dialog.open || this.dialogLoad !== load) {
      if (!scene.isConnected) this.sceneMeta.delete(scene);
      return;
    }
    this.dialog.removeAttribute('aria-busy');
    if (!loaded) {
      if (!scene.isConnected) this.sceneMeta.delete(scene);
      if (!wasOpen || !this.displayedPreviewCard) this.dialog.close();
      else {
        this.activePreviewCard = this.displayedPreviewCard;
        this.querySelector('[data-lifestyle-navigation-status]').textContent = 'Nie udało się wczytać zdjęcia. Spróbuj ponownie.';
      }
      return;
    }
    if (wasOpen) {
      this.clearExpanded();
      this.querySelector('[data-lifestyle-expanded]').replaceChildren(scene);
      this.observeScenes();
    }
    scene.style.visibility = '';
    this.displayedPreviewCard = card;
    this.updateCaption();
    this.refreshPlanes();
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('tapanda-lifestyle-gallery')) customElements.define('tapanda-lifestyle-gallery', TapandaLifestyleGallery);

